import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {withApplicationMetadata} from 'client/Routing/withApplicationMetadata';
import {withSubjectPageCopy} from 'client/Seo/subjectPageCopy';
import {StudyGuideItemList} from 'client/generic/StudyGuideItem/StudyGuideItem.react';
import {
  canCreateTemplateForSubject,
  getSubjectSubscriptionQuery,
  userLicensedSubjectsQuery,
  doesUserBelongToAnyLicensedClassrooms
} from 'lib/UserAccessUtil';
import {GuideModelV1} from 'resources/augmented/Guide/GuideModel.v1';
import {getVersionAgnosticResourceType} from 'resources/mandark.resource';
import sessionStore from 'client/Session/SessionStore';
import {useFluxStore} from 'client/framework';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal';
import deferComponentRender from 'lib/hocs/deferComponentRender';

import {showPublicViewForSubject} from 'components/PracticeView/practiceView.utils';

import {showGlobalLoginModal} from 'client/LogIn/utils';

import FRQGuideLevelItem from './FRQGuideLevelItem/FRQGuideLevelItem.react';
import FRQQuestionItem from './FRQQuestionItem/FRQQuestionItem.react';
import frqGuideUtils from './utils';

import '../free-response-question-guide.scss';

function isItemGuideLevel(item) {
  return (
    typeof item.getJsonApiType === 'function' &&
    getVersionAgnosticResourceType(item.getJsonApiType()) === 'guide_level'
  );
}

export function FreeResponseQuestionGuideView({guide, classrooms, subject}) {
  const [showUpgradeSubjectModal, setShowUpgradeSubjectModal] = useState(false);

  const subjectId = guide.getSubject().getId();
  /**
   * Bridged render-time reads. Both predicates reach into `sessionStore` and the mandark
   * state tree, so they re-evaluate on a store emit the way the class did via the global
   * `forceUpdate` cascade. Each selector returns a primitive — `canCreateTemplateForSubject`
   * allocates a fresh `{isReady, value}` object per call, which would never compare equal.
   */
  const usePublicView = useFluxStore(sessionStore, () => showPublicViewForSubject(subject));
  const canAssign = useFluxStore(sessionStore, () => canCreateTemplateForSubject(subjectId).value);
  const isReady = useFluxStore(sessionStore, () => canCreateTemplateForSubject(subjectId).isReady);

  useEffect(() => {
    if (
      sessionStore.hasValidSession() &&
      sessionStore.isStudent() &&
      ((!classrooms.isEmpty() && !subject.doesUserHaveAccess()) ||
        doesUserBelongToAnyLicensedClassrooms().value)
    ) {
      setShowUpgradeSubjectModal(true);
    }
    // Mirrors the class's `componentDidMount`: evaluated once against the mount-time props
    // and deliberately not re-run when `classrooms`/`subject` change, so a student who
    // dismisses the modal is not shown it again on the next store-driven re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavigationGating = useCallback((event) => {
    if (!sessionStore.hasValidSession()) {
      event.preventDefault();
      showGlobalLoginModal();
      return;
    }

    if (sessionStore.isStudent() && doesUserBelongToAnyLicensedClassrooms().value) {
      event.preventDefault();
      setShowUpgradeSubjectModal(true);
    }
  }, []);

  const getContent = () => {
    const subjectSlug = guide.getSubject().getUrlSlug();
    const fromGuidePath = `/${subjectSlug}/free-response`;
    const idsOfLastQuestionsOfThemes = frqGuideUtils.getIdsOfLastQuestionsOfThemes(
      guide,
      usePublicView
    );
    return frqGuideUtils.getCachedFreeResponseGuideItems(guide, isReady, (frqGuide) => {
      // Track the visually-preceding guide level — using it (rather than
      // guide.getGuideLevels().find()) keeps question links consistent for questions
      // duplicately aligned across guide levels, whose .find() iteration order differs
      // from the sort order shown here.
      let currentGuideLevel = null;
      return frqGuide.getSortedFRQItems(usePublicView).map((item, i) => {
        if (isItemGuideLevel(item)) {
          currentGuideLevel = item;
          const questionSets = item.getQuestionSets();
          if (questionSets.isEmpty()) {
            return null;
          }
          return (
            <FRQGuideLevelItem
              guideLevel={item}
              hasAssignAccess={canAssign}
              key={isReady ? i : item.getId()}
              linkTo={{
                pathname: `/learn/${subjectSlug}/${item.getUrlSlug()}`,
                state: {from: fromGuidePath}
              }}
              onClick={handleNavigationGating}
            />
          );
        }
        /**
         * This next block prevents us from applying the selector to a question
         * that appears last in one guide level but is duplicately aligned to another.
         * See https://github.com/albert-io/project-management/issues/1500
         */
        const nextItem = guide.getSortedFRQItems(usePublicView).get(i + 1);
        const isLastOfTheme =
          idsOfLastQuestionsOfThemes.includes(item.getId()) &&
          nextItem &&
          isItemGuideLevel(nextItem) &&
          nextItem.isTheme();
        const guideLevel =
          currentGuideLevel ||
          guide
            .getGuideLevels()
            .getCollection()
            .find((frqGuideLevel) => {
              return frqGuideLevel.getQuestionSets().find((set) => {
                const questions = usePublicView ? set.getPublicQuestions() : set.getQuestions();
                return questions.includes(item);
              });
            });
        const questionSet = guideLevel.getQuestionSets().find((set) => {
          const questions = usePublicView ? set.getPublicQuestions() : set.getQuestions();
          return questions.includes(item);
        });

        // Null `questionPath` on locked questions (redacted slug/title,
        // #19056) → row renders without a link wrapper.
        const setSlugPart = questionSet.getURLSlugPart();

        const questionPath = setSlugPart
          ? `/learn/${subjectSlug}/${guideLevel.getUrlSlug()}/${setSlugPart}`
          : null;

        const questionTo = questionPath
          ? {
              pathname: questionPath,
              state: {from: fromGuidePath}
            }
          : null;
        /**
         * We add **2** here to account for the `<HeroUnit>` containing a `h1` and levels starting at `h2`.
         */
        const headingLevel = guideLevel.getNlevel() + 2;
        return (
          <FRQQuestionItem
            className={isLastOfTheme ? 'sgi--last-of-theme' : null}
            question={item}
            questionSet={questionSet}
            to={questionTo}
            questionSetIds={[questionSet.getId()]}
            hasAssignAccess={canAssign}
            guideLevel={guideLevel}
            key={item.getId()}
            heading={`h${headingLevel}`}
            usePublicView={usePublicView}
            onClick={handleNavigationGating}
          />
        );
      });
    });
  };

  return (
    <>
      {!guide.getGuideLevels().getCollection().isEmpty() && (
        <div className='free-response-question-guide'>
          <StudyGuideItemList>{getContent()}</StudyGuideItemList>
        </div>
      )}
      {showUpgradeSubjectModal && (
        <UpgradeSubjectModal
          subject={subject}
          handleClose={() => setShowUpgradeSubjectModal(false)}
        />
      )}
    </>
  );
}

FreeResponseQuestionGuideView.propTypes = {
  guide: PropTypes.instanceOf(GuideModelV1),
  classrooms: ImmutablePropTypes.list,
  subject: PropTypes.object
};

const FreeResponseQuestionGuide = awaitMandarkQueries(
  ({subject}) => {
    const usePublicView = showPublicViewForSubject(subject);
    const queries = {
      guide: frqGuideUtils.getFrqGuideQuery({subjectId: subject.getId(), usePublicView}),
      classrooms: userLicensedSubjectsQuery()
    };
    if (sessionStore.hasValidSession()) {
      queries.subscriptions = getSubjectSubscriptionQuery(subject.getId());
    }
    return {queries};
  },
  withApplicationMetadata(
    deferComponentRender(FreeResponseQuestionGuideView),
    /**
     * Provide a custom `getMetadataFromProps` method for the FRQ view.
     *
     * @param props
     * @see https://github.com/albert-io/project-management/issues/1332
     * @see https://github.com/albert-io/project-management/issues/3131
     */
    (props) => {
      const subjectName = props.guide.getSubject().getName();
      return withSubjectPageCopy(subjectName, {
        title: `${subjectName} | Free Response Questions | Albert`,
        metaDescription: props.guide.getSubject().isAPSubject()
          ? `Original free-response prompts for ${subjectName} that mimic the questions found on the real exam. Our expert authors also provide an exemplary response for each AP free response question so students can better understand what AP graders look for.`
          : `Original free-response prompts for ${subjectName} that stimulate critical thinking through open-ended exploration and reasoning.`,
        canonicalPath: `/${props.guide.getSubject().getUrlSlug()}/free-response`
      });
    }
  )
);

export default FreeResponseQuestionGuide;
